// SCR-PUBLIC-030 (AP-ENG-02؛ US-ENG-071 و US-ENG-072): صفحه عمومی نظرسنجی با لینک توکن‌دار پیامک (/survey/:token) — بدون ورود، یک‌بار پاسخ، سه‌زبانه
// (فارسی، انگلیسی، عربی). NPS ۰ تا ۱۰ و رضایت پنج‌سطحی؛ پاسخ منفی ← پیام «پیگیری می‌کنیم» (BR-ENG-071)؛ NPS ۹ و ۱۰ ← دعوت اختیاری به ثبت نظر عمومی
// بدون هیچ مشوق (BR-ENG-072). لینک نامعتبر یا منقضی ← پیام عمومی بدون جزئیات.
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import {
  isApiError,
  unwrap,
  type EngPublicSurveyResultView,
  type EngSurveyQuestion,
  type EngSurveyText,
} from '@phoenix/api-client';
import { usePublicApi } from '@phoenix/auth';
import { SUPPORTED_LANGUAGES, useTranslation, type Language } from '@phoenix/i18n';
import { LoadingState } from '@phoenix/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

export const SCR_PUBLIC_030 = 'SCR-PUBLIC-030';

type AnswerValue = number | boolean | string;

/** متن پرسش به زبان انتخابی (fa پیش‌فرض). */
export function localized(text: EngSurveyText | null | undefined, language: Language): string {
  if (!text) {
    return '';
  }
  return (language === 'en' ? text.en : language === 'ar' ? text.ar : text.fa) || text.fa;
}

/** ارقام به خط زبان انتخابی (فارسی، عربی یا لاتین). */
function digits(value: number, language: Language): string {
  return new Intl.NumberFormat(language === 'fa' ? 'fa-IR' : language === 'ar' ? 'ar-EG' : 'en-US').format(
    value,
  );
}

export function PublicSurveyPage() {
  const { token = '' } = useParams();
  const { i18n } = useTranslation('public');
  const api = usePublicApi();
  const initial = (SUPPORTED_LANGUAGES as readonly string[]).includes(i18n.language)
    ? (i18n.language as Language)
    : 'fa';
  const [language, setLanguage] = useState<Language>(initial);
  const t = useMemo(() => i18n.getFixedT(language, 'public'), [i18n, language]);
  const dir = language === 'en' ? 'ltr' : 'rtl';
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});
  const [missing, setMissing] = useState<string[]>([]);
  const [result, setResult] = useState<EngPublicSurveyResultView | null>(null);

  useEffect(() => {
    document.title = t('public:survey.title');
  }, [t]);

  const survey = useQuery({
    queryKey: ['public-survey', token],
    queryFn: () =>
      unwrap(api.GET('/api/v1/engagement/public/surveys/{token}', { params: { path: { token } } })),
    retry: false,
  });

  const submit = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api/v1/engagement/public/surveys/{token}:submit', {
          params: { path: { token } },
          body: { answers: answers as never, language },
        }),
      ),
    onSuccess: setResult,
  });

  const questions: EngSurveyQuestion[] = survey.data?.questions ?? [];
  const set = (key: string, value: AnswerValue) => {
    setAnswers((current) => ({ ...current, [key]: value }));
    setMissing((current) => current.filter((item) => item !== key));
  };
  const send = () => {
    const required = questions
      .filter(
        (question) =>
          question.required && (answers[question.key] === undefined || answers[question.key] === ''),
      )
      .map((question) => question.key);
    setMissing(required);
    if (required.length === 0) {
      submit.mutate();
    }
  };

  const notFound = survey.error && isApiError(survey.error) && survey.error.status === 404;
  const conflict = submit.error && isApiError(submit.error) && submit.error.status === 409;

  return (
    <Box
      data-screen-id={SCR_PUBLIC_030}
      dir={dir}
      lang={language}
      sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 1.5, sm: 3 } }}
    >
      <Paper variant="outlined" component="main" sx={{ p: { xs: 2, sm: 4 }, maxWidth: 720, mx: 'auto' }}>
        <Stack
          direction="row"
          spacing={1}
          sx={{ mb: 2, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <LocalHospitalOutlinedIcon color="primary" />
            <Typography sx={{ fontWeight: 600 }} data-testid="survey-center">
              {[survey.data?.centerName, survey.data?.branchName].filter(Boolean).join(' · ') ||
                t('public:survey.center_fallback')}
            </Typography>
          </Stack>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={language}
            onChange={(_event, next: Language | null) => next && setLanguage(next)}
            aria-label={t('public:lang_label')}
          >
            {SUPPORTED_LANGUAGES.map((value) => (
              <ToggleButton key={value} value={value} lang={value} data-testid={`survey-lang-${value}`}>
                {t(`public:languages.${value}`)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
        <Typography variant="h1" sx={{ mb: 2, fontSize: '1.5rem' }}>
          {t('public:survey.title')}
        </Typography>

        {survey.isPending && <LoadingState rows={4} />}
        {survey.error && (
          <Alert severity="warning" data-testid="survey-invalid">
            {t(notFound ? 'public:survey.invalid' : 'public:survey.load_error')}
          </Alert>
        )}
        {survey.data?.status === 'Answered' && !result && (
          <Alert severity="info" data-testid="survey-already">
            {t('public:survey.already')}
          </Alert>
        )}
        {result && <SurveyThanks result={result} t={t} />}
        {survey.data?.status === 'Open' && !result && (
          <Stack
            spacing={3}
            component="form"
            noValidate
            onSubmit={(event) => (event.preventDefault(), send())}
            data-testid="survey-form"
          >
            <Typography variant="body2" color="text.secondary">
              {t('public:survey.intro')}
            </Typography>
            {questions.map((question) => (
              <QuestionField
                key={question.key}
                question={question}
                language={language}
                value={answers[question.key]}
                error={missing.includes(question.key)}
                onChange={(value) => set(question.key, value)}
                t={t}
              />
            ))}
            {missing.length > 0 && (
              <Alert severity="error" data-testid="survey-missing">
                {t('public:survey.required')}
              </Alert>
            )}
            {submit.error && (
              <Alert severity="error" data-testid="survey-submit-error">
                {t(conflict ? 'public:survey.already' : 'public:survey.submit_error')}
              </Alert>
            )}
            <Button
              type="submit"
              variant="contained"
              disabled={submit.isPending}
              sx={{ alignSelf: 'flex-start' }}
              data-testid="survey-submit"
            >
              {t('public:survey.submit')}
            </Button>
            <Typography variant="caption" color="text.secondary">
              {t('public:survey.privacy')}
            </Typography>
          </Stack>
        )}
      </Paper>
    </Box>
  );
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

function QuestionField({
  question,
  language,
  value,
  error,
  onChange,
  t,
}: {
  question: EngSurveyQuestion;
  language: Language;
  value: AnswerValue | undefined;
  error: boolean;
  onChange: (value: AnswerValue) => void;
  t: Translate;
}) {
  const label = localized(question.label, language);
  const id = `survey-q-${question.key}`;
  if (question.type === 'Text') {
    return (
      <TextField
        label={label}
        value={typeof value === 'string' ? value : ''}
        onChange={(event) => onChange(event.target.value)}
        required={question.required}
        error={error}
        helperText={error ? t('public:survey.required_one') : t('public:survey.no_medical')}
        multiline
        minRows={2}
        fullWidth
        slotProps={{ htmlInput: { maxLength: 1000, 'data-testid': id } }}
      />
    );
  }

  const scale =
    question.type === 'Nps'
      ? Array.from({ length: 11 }, (_item, index) => index)
      : question.type === 'Satisfaction'
        ? [5, 4, 3, 2, 1]
        : question.type === 'Scale'
          ? Array.from(
              { length: Number(question.max ?? 10) - Number(question.min ?? 0) + 1 },
              (_item, index) => Number(question.min ?? 0) + index,
            )
          : null;

  return (
    <FormControl error={error} required={question.required} component="fieldset" data-testid={id}>
      <FormLabel component="legend" sx={{ mb: 1 }}>
        {label}
      </FormLabel>
      {scale && question.type !== 'Satisfaction' && (
        <>
          <ToggleButtonGroup
            exclusive
            value={typeof value === 'number' ? value : null}
            onChange={(_event, next: number | null) => next !== null && onChange(next)}
            aria-label={label}
            sx={{ flexWrap: 'wrap', direction: 'ltr' }}
          >
            {scale.map((score) => (
              <ToggleButton
                key={score}
                value={score}
                sx={{ minWidth: 40 }}
                data-testid={`${id}-${score}`}
                aria-label={digits(score, language)}
              >
                {digits(score, language)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {question.type === 'Nps' && (
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
              <Typography variant="caption">{t('public:survey.nps_low')}</Typography>
              <Typography variant="caption">{t('public:survey.nps_high')}</Typography>
            </Stack>
          )}
        </>
      )}
      {question.type === 'Satisfaction' && (
        <RadioGroup
          row
          value={typeof value === 'number' ? String(value) : ''}
          onChange={(event) => onChange(Number(event.target.value))}
        >
          {[5, 4, 3, 2, 1].map((score) => (
            <FormControlLabel
              key={score}
              value={String(score)}
              control={<Radio />}
              label={t(`public:survey.satisfaction.${score}`)}
              data-testid={`${id}-${score}`}
            />
          ))}
        </RadioGroup>
      )}
      {question.type === 'YesNo' && (
        <RadioGroup
          row
          value={typeof value === 'boolean' ? String(value) : ''}
          onChange={(event) => onChange(event.target.value === 'true')}
        >
          <FormControlLabel value="true" control={<Radio />} label={t('public:survey.yes')} />
          <FormControlLabel value="false" control={<Radio />} label={t('public:survey.no')} />
        </RadioGroup>
      )}
      {question.type === 'SingleChoice' && (
        <RadioGroup
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => onChange(event.target.value)}
        >
          {(question.options ?? []).map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio />}
              label={localized(option.label, language)}
            />
          ))}
        </RadioGroup>
      )}
      {error && <FormHelperText>{t('public:survey.required_one')}</FormHelperText>}
    </FormControl>
  );
}

function SurveyThanks({ result, t }: { result: EngPublicSurveyResultView; t: Translate }) {
  const links = result.reviewInvite?.links ?? [];
  return (
    <Stack spacing={2} data-testid="survey-thanks">
      <Alert severity="success">{t('public:survey.thanks')}</Alert>
      {result.negative && (
        <Alert severity="info" data-testid="survey-followup">
          {t('public:survey.negative')}
        </Alert>
      )}
      {links.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }} data-testid="survey-review-invite">
          <Typography sx={{ mb: 1 }}>{t('public:survey.review_invite')}</Typography>
          <Stack spacing={1}>
            {links.map((link) => (
              <Link
                key={link.url}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                dir="ltr"
                data-testid={`survey-review-${link.kind}`}
              >
                {link.kind === 'Custom'
                  ? link.label || link.url
                  : t(`public:survey.review_kind.${link.kind}`)}
              </Link>
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t('public:survey.review_optional')}
          </Typography>
        </Paper>
      )}
    </Stack>
  );
}
